/** Terrace fence: bays of the Blender `fence_segment` asset laid along the terrace edge (pure layout + builder). */
import * as THREE from 'three';
import { STAIR_RAIL_HEADS } from './composition';

export type Point2 = [number, number]; // world (x, z)

const head = (p: readonly number[]): Point2 => [p[0]!, p[2]!];

/**
 * Three runs along every terrace edge that has a drop, each with both ends against something (tested):
 *  1. from the haiden's front corner along the front edge and the jut's left edge into the left stair rail;
 *  2. from the right stair rail back along the right edge to the cut wall at the back;
 *  3. from the cut wall at the back along the left edge, round the honden reserve, to the haiden's back corner.
 * The haiden's seaward face stands at the edge itself. Inset 0.35 m from the edge; the torii stands inside, on the
 * stairs head.
 */
export const FENCE_PATHS: Point2[][] = [
  [[-3.74, -3.26], [1.5, -2.8], [1.2, -8], head(STAIR_RAIL_HEADS.left)],
  [head(STAIR_RAIL_HEADS.right), [3.55, 3.5], [3.55, 9.65]],
  [[-15.65, 9.65], [-15.85, -9.45], [-12.1, -13.3], [-8.68, -9.93], [-8.25, -7.7]],
];

export interface Bay {
  start: Point2;
  end: Point2;
  lengthM: number;
  /** three.js yaw so the asset's local +X runs from start to end. */
  yawDeg: number;
}

/** Splits each leg into equal bays no longer than maxBayM. */
export function fenceBays(path: Point2[], maxBayM: number): Bay[] {
  const bays: Bay[] = [];
  for (let i = 0; i + 1 < path.length; i++) {
    const [ax, az] = path[i]!;
    const [bx, bz] = path[i + 1]!;
    const len = Math.hypot(bx - ax, bz - az);
    const n = Math.max(1, Math.ceil(len / maxBayM - 1e-9));
    const yawDeg = (Math.atan2(-(bz - az), bx - ax) * 180) / Math.PI;
    for (let k = 0; k < n; k++) {
      const t0 = k / n;
      const t1 = (k + 1) / n;
      bays.push({
        start: [ax + (bx - ax) * t0, az + (bz - az) * t0],
        end: [ax + (bx - ax) * t1, az + (bz - az) * t1],
        lengthM: len / n,
        yawDeg,
      });
    }
  }
  return bays;
}

const SEGMENT_M = 2;

const atRailHead = (p: Point2): boolean =>
  [STAIR_RAIL_HEADS.left, STAIR_RAIL_HEADS.right].some((h) => Math.hypot(p[0] - h[0], p[1] - h[2]) < 1e-3);

/**
 * Clones the fence bay asset along every path (x-scaled to fit), plus a closing post at each path end — except at a
 * stair rail head, where the stairs' own head post stands (two posts there read as one sunk beside another).
 */
export function buildFence(segment: THREE.Object3D): THREE.Group {
  const group = new THREE.Group();
  group.name = 'fence';
  const post = segment.getObjectByName('fence_segment_post');
  for (const path of FENCE_PATHS) {
    const bays = fenceBays(path, SEGMENT_M);
    bays.forEach((bay, i) => {
      const bayObj = segment.clone(true);
      bayObj.position.set(bay.start[0], 0, bay.start[1]);
      bayObj.rotation.y = (bay.yawDeg * Math.PI) / 180;
      // Stretch through a holder pivoted at the post: the rails node itself carries meshopt's quantization
      // transform, which must not be overwritten (and scaling it would pivot on its own offset, not the post).
      const rails = bayObj.getObjectByName('fence_segment_rails');
      if (rails) {
        const holder = new THREE.Group();
        rails.parent!.add(holder);
        holder.attach(rails);
        holder.scale.x = bay.lengthM / SEGMENT_M;
      }
      const bayPost = bayObj.getObjectByName('fence_segment_post');
      if (i === 0 && bayPost && atRailHead(bay.start)) bayPost.visible = false;
      group.add(bayObj);
    });
    const last = bays[bays.length - 1];
    if (last && post && !atRailHead(last.end)) {
      // position a holder, not the post: its node carries meshopt's quantization offset (overwriting it sank the post)
      const closing = new THREE.Group();
      closing.name = 'fence_closing_post';
      closing.position.set(last.end[0], 0, last.end[1]);
      closing.add(post.clone(true));
      group.add(closing);
    }
  }
  return group;
}
